import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PeopleIcon from '@mui/icons-material/People';
import { Box, Chip, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { SwitchableTenantHierarchyDTO } from 'probonio-shared-ui/api';
import { useTranslation } from 'react-i18next';

interface Props {
  hierarchyNode: SwitchableTenantHierarchyDTO;
  currentTenantId: string;
  selectedTenantId: string;
  expanded: Record<string, boolean>;
  level?: number;
  onTenantSelect: (tenantId: string) => void;
  onToggleExpand: (level: number, tenantId: string) => void;
}

const Bullet: React.FC = () => <Typography sx={{ marginTop: 0.25 }}>&bull;</Typography>;

export default function TenantTreeNode({ level = 0, ...props }: Props) {
  const theme = useTheme();
  const { t } = useTranslation('tenantHierarchy');

  const tenant = props.hierarchyNode.tenant;
  const paddingLeft = level * 1.5;
  const showCarret = !!props.hierarchyNode.children.length;
  const isExpanded = !!props.expanded[tenant.id];
  const isSelected = props.selectedTenantId === tenant.id;
  // Both counts are admin fields, so they are absent whenever the caller resolved the tenant
  // without them. Rendering them anyway would claim a confident "0 employees, 0 benefits".
  const showCounts = tenant.employeeCount !== undefined && tenant.activeBenefits !== undefined;

  return (
    <Box data-test-id="tenant-tree-node">
      <Box
        sx={{
          paddingRight: 1,
          paddingLeft: 0.5,
          paddingY: 0.5,
          borderRadius: 1,
          cursor: 'pointer',
          backgroundColor: isSelected ? theme.palette.secondary.main : 'transparent',
          transition: 'background-color 0.3s',
          '&:hover': !isSelected
            ? { backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[800] }
            : {},
        }}
        onClick={() => props.onTenantSelect(tenant.id)}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            paddingLeft: paddingLeft,
          }}
        >
          {showCarret && (
            <IconButton
              onClick={e => {
                e.stopPropagation();
                props.onToggleExpand(level, tenant.id);
              }}
              data-test-id="tenant-tree-node-toggle"
            >
              <ExpandMoreIcon
                sx={{
                  transition: 'transform 0.2s',
                  transform: `rotate(${isExpanded ? 0 : -90}deg)`,
                }}
              />
            </IconButton>
          )}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flex: 1,
            }}
          >
            <Box
              sx={{
                paddingLeft: showCarret ? 0 : 4,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 'medium',
                  color: 'text.primary',
                }}
              >
                {tenant.name}
              </Typography>
              {(showCounts || tenant.bikeleasingCompanyCode) && (
                <Box
                  sx={{
                    color: 'text.secondary',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  {tenant.bikeleasingCompanyCode && (
                    <>
                      <Typography variant="body2" data-test-id="tenant-tree-node-company-code">
                        {tenant.bikeleasingCompanyCode}
                      </Typography>
                      {showCounts && <Bullet />}
                    </>
                  )}
                  {showCounts && (
                    <>
                      <PeopleIcon sx={{ fontSize: theme => theme.typography.body1.fontSize }} />
                      <Typography variant="body2">{t('employeesCount', { count: tenant.employeeCount })}</Typography>
                      <Bullet />
                      <CardGiftcardIcon sx={{ fontSize: theme => theme.typography.body1.fontSize }} />
                      <Typography variant="body2">{t('benefitsCount', { count: tenant.activeBenefits!.length })}</Typography>
                    </>
                  )}
                </Box>
              )}
            </Box>
            {props.currentTenantId === tenant.id && <Chip label={t('activeTenant')} color="default" />}
          </Box>
        </Stack>
      </Box>
      {isExpanded &&
        props.hierarchyNode.children.map(child => (
          <TenantTreeNode
            key={child.tenant.id}
            hierarchyNode={child}
            currentTenantId={props.currentTenantId}
            selectedTenantId={props.selectedTenantId}
            expanded={props.expanded}
            level={level + 1}
            onTenantSelect={props.onTenantSelect}
            onToggleExpand={props.onToggleExpand}
          />
        ))}
    </Box>
  );
}
